import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import api from '../api';
import Toast from '../components/Toast';

/**
 * AdminUsers — /admin/users
 * Table: search + status filter + suspend/reinstate + CSV export.
 */
const STATUS_OPTIONS = [
  { key: 'all',       label: 'All' },
  { key: 'active',    label: 'Active' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'admin',     label: 'Admins' },
  { key: 'demo',      label: 'Demo' },
];

/** ISO-2 country code → flag emoji via regional-indicator code points. */
function countryFlag(iso2) {
  if (!iso2 || iso2.length !== 2) return '';
  const base = 0x1F1E6;
  return String.fromCodePoint(base + (iso2.charCodeAt(0) - 65), base + (iso2.charCodeAt(1) - 65));
}

export default function AdminUsers() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(50);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [toast, setToast] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [grantTarget, setGrantTarget] = useState(null); // user obj being granted
  const [grantAmount, setGrantAmount] = useState('');
  const [grantReason, setGrantReason] = useState('');
  const [grantBusy, setGrantBusy] = useState(false);

  // debounce search input (300ms)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = {
        page, page_size: pageSize, status, sort_by: sortBy, sort_dir: sortDir,
      };
      if (debounced) params.search = debounced;
      const { data } = await api.get('/admin/users', { params });
      setRows(data.users || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      // If the server clamped our page (e.g. filter shrank results), reflect it
      if (data.page && data.page !== page) setPage(data.page);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, debounced, page, pageSize, sortBy, sortDir]);

  // Reset to page 1 whenever filters/search/sort change
  useEffect(() => { setPage(1); }, [status, debounced, sortBy, sortDir]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const doSuspend = async (u) => {
    if (busyId) return;
    setBusyId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/suspend`);
      setToast({ msg: `Suspended @${u.handle || u.email}`, tone: 'warn' });
      fetchUsers();
    } catch (e) {
      setToast({ msg: e?.response?.data?.detail || 'Suspend failed', tone: 'error' });
    } finally { setBusyId(null); }
  };

  const doSendPlaybook = async (u) => {
    if (busyId) return;
    setBusyId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/send-playbook`);
      setToast({ msg: `📘 Playbook emailed to ${u.email}`, tone: 'info' });
    } catch (e) {
      setToast({ msg: e?.response?.data?.detail || 'Send failed', tone: 'error' });
    } finally { setBusyId(null); }
  };

  const doReinstate = async (u) => {
    if (busyId) return;
    setBusyId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/reinstate`);
      setToast({ msg: `Reinstated @${u.handle || u.email}`, tone: 'info' });
      fetchUsers();
    } catch (e) {
      setToast({ msg: e?.response?.data?.detail || 'Reinstate failed', tone: 'error' });
    } finally { setBusyId(null); }
  };

  const doImpersonate = async (u) => {
    // Real users get a hard confirm — typing CONFIRM avoids accidental takeovers.
    // Seed / test / demo accounts skip the extra prompt (existing dev flow).
    const isFixture = u.is_demo === true || u.is_seed === true || u.is_test === true;
    if (!isFixture) {
      const typed = window.prompt(
        `Impersonate @${u.handle || u.email} in READ-ONLY mode?\n\n` +
        `You will see their UI exactly as they do (feed, wallet, DMs, notifications) but every action button will fail with a 403. Every impersonation is logged.\n\n` +
        `Type CONFIRM to proceed.`
      );
      if (typed !== 'CONFIRM') return;
    } else if (!window.confirm(`Impersonate @${u.handle || u.email}? Your admin session will be paused.`)) {
      return;
    }
    setBusyId(u.id);
    try {
      const { data } = await api.post(`/admin/seed/users/${u.id}/impersonate`, { ttl_minutes: 120 });
      const TOKEN_KEY = 'clanster_access_token';
      const adminToken = localStorage.getItem(TOKEN_KEY);
      if (adminToken) localStorage.setItem('admin_backup_token', adminToken);
      localStorage.setItem(TOKEN_KEY, data.access_token);
      localStorage.setItem('impersonating_user_id', u.id);
      localStorage.setItem('impersonating_handle', u.handle || '');
      localStorage.setItem('impersonating_readonly', data.shadow_readonly ? '1' : '0');
      window.location.href = '/home';
    } catch (e) {
      setToast({ msg: 'Impersonation failed: ' + (e?.response?.data?.detail || e.message), tone: 'error' });
      setBusyId(null);
    }
  };

  const openGrant = (u) => {
    setGrantTarget(u);
    setGrantAmount('');
    setGrantReason('');
  };
  const closeGrant = () => {
    if (grantBusy) return;
    setGrantTarget(null);
  };
  const submitGrant = async () => {
    if (!grantTarget || grantBusy) return;
    const amt = Number(grantAmount);
    if (!Number.isFinite(amt) || amt === 0) {
      setToast({ msg: 'Enter a non-zero amount (positive to credit, negative to debit).', tone: 'error' });
      return;
    }
    if (Math.abs(amt) > 1_000_000) {
      setToast({ msg: 'Max ±1,000,000 pts per grant.', tone: 'error' });
      return;
    }
    setGrantBusy(true);
    try {
      const { data } = await api.post(
        `/admin/users/${grantTarget.id}/points-grant`,
        { amount: amt, reason: grantReason.trim() || null },
      );
      setToast({
        msg: `${amt > 0 ? '+' : ''}${data.delta.toFixed(2)} pts → @${grantTarget.handle || grantTarget.email}. New balance: ${data.new_balance.toLocaleString()} pts.`,
        tone: 'success',
      });
      closeGrant();
      fetchUsers();
    } catch (e) {
      setToast({ msg: e?.response?.data?.detail || 'Grant failed', tone: 'error' });
    } finally { setGrantBusy(false); }
  };

  const doExport = async () => {
    try {
      const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      const token = localStorage.getItem('clanster_access_token');
      const res = await fetch(`${base}/api/admin/users/export/csv`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clanster_users_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setToast({ msg: 'CSV downloaded 🎉', tone: 'success' });
    } catch (e) {
      setToast({ msg: 'Export failed. Try again.', tone: 'error' });
    }
  };

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };
  const sortArrow = (col) => sortBy === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  return (
    <AdminLayout active="users">
      <div className="admin-page" data-testid="admin-users-page">
        <header className="admin-page-header row">
          <div>
            <h1 className="admin-page-title">Users</h1>
            <p className="admin-page-sub">
              {total.toLocaleString()} total
              {debounced ? ` · matching "${debounced}"` : ''}
              {totalPages > 1 ? ` · page ${page} of ${totalPages}` : ''}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              className="admin-btn-primary"
              onClick={() => navigate('/admin/points-log')}
              data-testid="admin-points-log-link"
              style={{ background: 'linear-gradient(135deg, #3b3d47, #12151c)' }}
            >
              📖 Points Log
            </button>
            <button
              type="button"
              className="admin-btn-primary"
              onClick={doExport}
              data-testid="admin-export-csv"
            >
              ⬇️ Export CSV
            </button>
          </div>
        </header>

        <div className="admin-toolbar">
          <input
            type="text"
            className="admin-search"
            placeholder="Search email, name, or @handle…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="admin-users-search"
          />
          <div className="admin-chip-row">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                className={`admin-chip${status === opt.key ? ' active' : ''}`}
                onClick={() => setStatus(opt.key)}
                data-testid={`filter-${opt.key}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {err && <div className="admin-error" data-testid="admin-users-error">{err}</div>}
        {loading && <div className="admin-loading">Loading…</div>}

        {!loading && !err && (
          rows.length === 0 ? (
            <div className="admin-empty" data-testid="admin-users-empty">No users match.</div>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table" data-testid="admin-users-table">
                <thead>
                  <tr>
                    <th className="admin-th-sort" onClick={() => toggleSort('display_name')} data-testid="sort-user">
                      User{sortArrow('display_name')}
                    </th>
                    <th className="admin-th-sort" onClick={() => toggleSort('email')} data-testid="sort-email">
                      Email{sortArrow('email')}
                    </th>
                    <th>Role</th>
                    <th title="User-declared country (from Profile Setup phone dropdown)">Country</th>
                    <th title="User-declared phone number">Phone</th>
                    <th title="IP-derived country (auto-detected on login)">IP Country</th>
                    <th
                      className="admin-th-sort"
                      style={{ textAlign: 'right' }}
                      onClick={() => toggleSort('points_balance')}
                      data-testid="sort-points"
                    >
                      Points{sortArrow('points_balance')}
                    </th>
                    <th
                      className="admin-th-sort"
                      style={{ textAlign: 'right' }}
                      onClick={() => toggleSort('bit_price')}
                      data-testid="sort-bit-price"
                      title="Current Bit price (pts) — creators only"
                    >
                      Bit Price{sortArrow('bit_price')}
                    </th>
                    <th
                      className="admin-th-sort"
                      style={{ textAlign: 'right' }}
                      onClick={() => toggleSort('lifetime_value')}
                      data-testid="sort-lifetime-value"
                      title="Lifetime access value (pts) — set by creator at mint"
                    >
                      LTV{sortArrow('lifetime_value')}
                    </th>
                    <th
                      className="admin-th-sort"
                      style={{ textAlign: 'right' }}
                      onClick={() => toggleSort('supply_cap')}
                      data-testid="sort-supply-cap"
                      title="Total Bits available"
                    >
                      Supply{sortArrow('supply_cap')}
                    </th>
                    <th
                      className="admin-th-sort"
                      style={{ textAlign: 'right' }}
                      onClick={() => toggleSort('clan_fill_pct')}
                      data-testid="sort-fill-pct"
                      title="% of Bits minted vs supply cap"
                    >
                      Fill %{sortArrow('clan_fill_pct')}
                    </th>
                    <th
                      className="admin-th-sort"
                      style={{ textAlign: 'right' }}
                      onClick={() => toggleSort('holders_count')}
                      data-testid="sort-holders"
                      title="Number of holders meeting min-Bits-to-join"
                    >
                      Holders{sortArrow('holders_count')}
                    </th>
                    <th>Status</th>
                    <th className="admin-th-sort" onClick={() => toggleSort('created_at')} data-testid="sort-joined">
                      Joined{sortArrow('created_at')}
                    </th>
                    <th style={{ width: 160 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u) => (
                    <tr key={u.id} data-testid={`admin-user-row-${u.id}`}>
                      <td>
                        <div className="admin-user-cell">
                          <span className="admin-user-name-cell">{u.display_name || '(no name)'}</span>
                          {u.verified && <span className="admin-verified">✓</span>}
                          {u.handle && <span className="admin-user-handle">@{u.handle}</span>}
                        </div>
                      </td>
                      <td className="admin-mono admin-small admin-email-cell" data-testid={`email-${u.id}`}>{u.email}</td>
                      <td>
                        {u.role === 'admin'
                          ? <span className="admin-tag tag-admin">Admin</span>
                          : u.is_demo
                          ? <span className="admin-tag tag-demo">Demo</span>
                          : <span className="admin-tag">User</span>}
                      </td>
                      <td className="admin-mono admin-small" data-testid={`phone-country-${u.id}`}>
                        {u.phone_country_code
                          ? (
                            <span title={u.phone_country_code}>
                              {countryFlag(u.phone_country_code)} {u.phone_country_code}
                            </span>
                          )
                          : <span className="admin-muted">—</span>}
                      </td>
                      <td className="admin-mono admin-small" data-testid={`phone-${u.id}`}>
                        {u.phone_number || <span className="admin-muted">—</span>}
                      </td>
                      <td className="admin-mono admin-small" data-testid={`country-${u.id}`}>
                        {u.country_code
                          ? (
                            <span title={u.country_name || u.country_code}>
                              {countryFlag(u.country_code)} {u.country_code}
                            </span>
                          )
                          : <span className="admin-muted">—</span>}
                      </td>
                      <td style={{ textAlign: 'right' }}>{u.points_balance.toLocaleString()}</td>
                      <td style={{ textAlign: 'right' }} className="admin-mono admin-small" data-testid={`bit-price-${u.id}`}>
                        {u.bit_price != null ? u.bit_price.toFixed(4) : <span className="admin-muted">—</span>}
                      </td>
                      <td style={{ textAlign: 'right' }} className="admin-mono admin-small" data-testid={`ltv-${u.id}`}>
                        {u.lifetime_value != null ? u.lifetime_value.toLocaleString() : <span className="admin-muted">—</span>}
                      </td>
                      <td style={{ textAlign: 'right' }} className="admin-mono admin-small" data-testid={`supply-${u.id}`}>
                        {u.curve_type === 'sqrt'
                          ? <span className="admin-muted" title="sqrt curve — unlimited supply">∞</span>
                          : u.supply_cap != null ? u.supply_cap.toLocaleString() : <span className="admin-muted">—</span>}
                      </td>
                      <td style={{ textAlign: 'right' }} className="admin-mono admin-small" data-testid={`fill-${u.id}`}>
                        {u.curve_type === 'sqrt'
                          ? <span className="admin-muted" title="sqrt curve has no fill %">—</span>
                          : u.clan_fill_pct != null ? `${u.clan_fill_pct.toFixed(1)}%` : <span className="admin-muted">—</span>}
                      </td>
                      <td style={{ textAlign: 'right' }} className="admin-mono admin-small" data-testid={`holders-${u.id}`}>
                        {u.holders_count != null ? u.holders_count.toLocaleString() : <span className="admin-muted">—</span>}
                      </td>
                      <td>
                        {u.suspended
                          ? <span className="admin-tag tag-warn">Suspended</span>
                          : <span className="admin-tag tag-ok">Active</span>}
                      </td>
                      <td className="admin-mono admin-small">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                          {u.role !== 'admin' && (
                            <button
                              type="button"
                              className="admin-btn-sm"
                              style={{ background: 'linear-gradient(135deg, #FF5722, #E040FB)', color: '#fff', border: 'none' }}
                              onClick={() => openGrant(u)}
                              data-testid={`grant-${u.id}`}
                            >
                              💰 Grant
                            </button>
                          )}
                          {u.role !== 'admin' && u.email && (
                            <button
                              type="button"
                              className="admin-btn-sm"
                              onClick={() => doSendPlaybook(u)}
                              disabled={busyId === u.id}
                              data-testid={`playbook-${u.id}`}
                              title="Email the Creator Launch Playbook"
                            >
                              📘 Playbook
                            </button>
                          )}
                          {u.role === 'admin' ? (
                            <span className="admin-small admin-muted">—</span>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="admin-btn-sm"
                                style={{ background: 'rgba(139, 92, 246, 0.18)', color: '#C4B5FD', border: '1px solid rgba(139, 92, 246, 0.35)' }}
                                onClick={() => doImpersonate(u)}
                                disabled={busyId === u.id}
                                data-testid={`impersonate-${u.id}`}
                                title="Impersonate this user (read-only for real users)"
                              >
                                🎭 Impersonate
                              </button>
                              {u.suspended ? (
                                <button
                                  type="button"
                                  className="admin-btn-sm ok"
                                  onClick={() => doReinstate(u)}
                                  disabled={busyId === u.id}
                                  data-testid={`reinstate-${u.id}`}
                                >
                                  {busyId === u.id ? '…' : 'Reinstate'}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="admin-btn-sm warn"
                                  onClick={() => doSuspend(u)}
                                  disabled={busyId === u.id}
                                  data-testid={`suspend-${u.id}`}
                                >
                                  {busyId === u.id ? '…' : 'Suspend'}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {/* Pagination */}
        {!loading && !err && totalPages > 1 && (
          <div className="admin-pager" data-testid="admin-users-pager">
            <button
              type="button"
              className="admin-pager-btn"
              onClick={() => setPage(1)}
              disabled={page === 1}
              data-testid="pager-first"
            >« First</button>
            <button
              type="button"
              className="admin-pager-btn"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              data-testid="pager-prev"
            >‹ Prev</button>
            {(() => {
              // Show up to 5 numbered buttons around the current page
              const nums = [];
              const start = Math.max(1, Math.min(page - 2, totalPages - 4));
              const end = Math.min(totalPages, start + 4);
              for (let i = start; i <= end; i++) nums.push(i);
              return nums.map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`admin-pager-btn${n === page ? ' active' : ''}`}
                  onClick={() => setPage(n)}
                  data-testid={`pager-${n}`}
                >{n}</button>
              ));
            })()}
            <button
              type="button"
              className="admin-pager-btn"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              data-testid="pager-next"
            >Next ›</button>
            <button
              type="button"
              className="admin-pager-btn"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              data-testid="pager-last"
            >Last »</button>
          </div>
        )}
      </div>
      {toast && <Toast msg={toast.msg} tone={toast.tone} onDone={() => setToast(null)} />}

      {grantTarget && (
        <>
          <div className="ssm-scrim" onClick={closeGrant} data-testid="grant-modal-scrim" />
          <div className="ssm-modal" role="dialog" aria-modal="true" data-testid="grant-modal" style={{ maxWidth: 420 }}>
            <button
              className="ssm-close"
              onClick={closeGrant}
              aria-label="Close"
              data-testid="grant-modal-close"
              disabled={grantBusy}
            >
              <svg viewBox="0 0 16 16" fill="none" width="12" height="12">
                <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
            <div className="ssm-header">
              <div className="ssm-creator-name">💰 Grant Points</div>
              <div className="ssm-creator-handle">
                @{grantTarget.handle || grantTarget.email} · current: {grantTarget.points_balance?.toLocaleString?.() || 0} pts
              </div>
            </div>

            <div className="field">
              <label className="field-label" htmlFor="grant-amount-input">
                <span>Amount (pts)</span>
                <span className="char-count">use negative to debit</span>
              </label>
              <input
                id="grant-amount-input"
                type="number"
                step="0.01"
                min="-1000000"
                max="1000000"
                className="field-input"
                placeholder="e.g. 100 or -25"
                value={grantAmount}
                onChange={(e) => setGrantAmount(e.target.value)}
                autoFocus
                disabled={grantBusy}
                data-testid="grant-amount-input"
              />
            </div>

            <div className="field" style={{ marginTop: 12 }}>
              <label className="field-label" htmlFor="grant-reason-input">
                <span>Reason (optional)</span>
                <span className="char-count">{grantReason.length}/200</span>
              </label>
              <input
                id="grant-reason-input"
                type="text"
                maxLength={200}
                className="field-input"
                placeholder="e.g. Beta tester reward, refund for issue #123"
                value={grantReason}
                onChange={(e) => setGrantReason(e.target.value)}
                disabled={grantBusy}
                data-testid="grant-reason-input"
              />
            </div>

            <button
              className="ssm-primary-close"
              onClick={submitGrant}
              disabled={grantBusy || !grantAmount.trim() || Number(grantAmount) === 0}
              data-testid="grant-submit"
              style={{ marginTop: 18 }}
            >
              {grantBusy ? 'Applying…'
                : Number(grantAmount) < 0
                  ? `Debit ${Math.abs(Number(grantAmount)).toFixed(2)} pts →`
                  : `Grant ${(Number(grantAmount) || 0).toFixed(2)} pts →`}
            </button>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
